import type { CampInsert, CampUpdate } from '#shared/types'
import type { CampView } from '#shared/types/camps'
import type { CampFormsResponse } from '#shared/types/forms'
import { useAdminFiltersStore } from '~/stores/adminFilters'
import { $api } from '~/utils/api'

import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface CampFormsApiResponse {
  success: boolean
  data: CampFormsResponse
}

interface CampsFilters {
  page?: number
  limit?: number
  search?: string
  categoryId?: string
  status?: 'draft' | 'published' | 'cancelled' | 'completed'
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface CampsResponse {
  data: CampView[]
  total: number
  page: number
  limit: number
}

export const useCamps = () => {
  /**
   * Get a list of camps with optional filters
   */
  const getCamps = async (filters: CampsFilters = {}): Promise<CampsResponse> => {
    try {
      const adminFilters = useAdminFiltersStore()
      const queryString = buildQueryString({ ...adminFilters.toQueryParams(), ...filters })
      const url = `/api/camps${queryString ? `?${queryString}` : ''}`

      return await $api<CampsResponse>(url)
    }
    catch (error) {
      logApiError('/api/camps', error, { filters })
      throw error
    }
  }

  /**
   * Get a single camp by ID
   */
  const getCamp = async (id: string): Promise<CampView> => {
    try {
      const response = await $api<{ success: boolean, data: CampView }>(`/api/camps/${id}`)

      return response.data
    }
    catch (error) {
      logApiError(`/api/camps/${id}`, error, { id })
      throw error
    }
  }

  /**
   * Create a new camp
   */
  const createCamp = async (data: CampInsert): Promise<CampView> => {
    const response = await $api<{ success: boolean, data: CampView }>('/api/camps', {
      method: 'POST',
      body: data,
    })

    return response.data
  }

  /**
   * Update an existing camp
   */
  const updateCamp = async (id: string, data: CampUpdate): Promise<CampView> => {
    const response = await $api<{ success: boolean, data: CampView }>(`/api/camps/${id}`, {
      method: 'PATCH',
      body: data,
    })

    return response.data
  }

  /**
   * Delete a camp
   */
  const deleteCamp = async (id: string): Promise<void> => {
    return await $api<void>(`/api/camps/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Get published camps (public endpoint)
   */
  const getPublishedCamps = async (filters: Omit<CampsFilters, 'status'> = {}): Promise<CampsResponse> => {
    return await getCamps({ ...filters, status: 'published' })
  }

  /**
   * Duplicate a camp
   */
  const duplicateCamp = async (id: string): Promise<CampView> => {
    const response = await $api<{ success: boolean, data: CampView }>(`/api/camps/${id}/duplicate`, {
      method: 'POST',
    })

    return response.data
  }

  /**
   * Publish a camp
   */
  const publishCamp = async (id: string): Promise<CampView> => {
    return await updateCamp(id, { status: 'published' })
  }

  /**
   * Archive a camp (set to completed status)
   */
  const archiveCamp = async (id: string): Promise<CampView> => {
    return await updateCamp(id, { status: 'completed' })
  }

  /**
   * Bulk archive multiple camps
   */
  const bulkArchiveCamps = async (ids: string[]): Promise<{ archived: number }> => {
    try {
      return await $api<{ archived: number }>('/api/camps/bulk-archive', {
        method: 'POST',
        body: { camp_ids: ids },
      })
    }
    catch (error) {
      logApiError('/api/camps/bulk-archive', error, { ids })
      throw error
    }
  }

  /**
   * Get forms linked to a camp
   */
  const getCampForms = async (campId: string): Promise<CampFormsApiResponse> => {
    try {
      return await $api<CampFormsApiResponse>(`/api/camps/${campId}/forms`)
    }
    catch (error) {
      logApiError(`/api/camps/${campId}/forms`, error, { campId })
      throw error
    }
  }

  return {
    getCamps,
    getCamp,
    createCamp,
    updateCamp,
    deleteCamp,
    getPublishedCamps,
    duplicateCamp,
    publishCamp,
    archiveCamp,
    bulkArchiveCamps,
    getCampForms,
  }
}
